import { useState, useEffect } from 'react';
import { cloudSaveService } from '../services/CloudSaveService';
import type { CloudSave } from '../services/CloudSaveService';
import toast from 'react-hot-toast';
import '../styles/save-manager.css';

export default function SaveManager() {
  const [saves, setSaves] = useState<CloudSave[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [groupedSaves, setGroupedSaves] = useState<Map<string, CloudSave[]>>(
    new Map()
  );

  useEffect(() => {
    loadSaves();
  }, []);

  useEffect(() => {
    // Group saves by ROM hash
    const grouped = new Map<string, CloudSave[]>();
    saves.forEach((save) => {
      const existing = grouped.get(save.romHash) || [];
      existing.push(save);
      grouped.set(save.romHash, existing);
    });
    setGroupedSaves(grouped);
  }, [saves]);

  const loadSaves = async () => {
    try {
      setIsLoading(true);
      const allSaves = await cloudSaveService.getAllSaves();
      setSaves(allSaves);
    } catch (error) {
      console.error('Failed to load saves:', error);
      toast.error('Failed to load save files');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadSave = async (save: CloudSave) => {
    try {
      toast.loading('Downloading save...', { id: 'save-download' });
      const saveData = await cloudSaveService.downloadSave(save.id);

      // Create download link
      const blob = new Blob([saveData as BlobPart], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = save.romName
        ? `${save.romName.replace(/[^a-z0-9]/gi, '_')}_slot${save.saveSlot}.sav`
        : `save_${save.romHash.substring(0, 8)}_slot${save.saveSlot}.sav`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.dismiss('save-download');
      toast.success('Save downloaded successfully');
    } catch (error) {
      console.error('Failed to download save:', error);
      toast.dismiss('save-download');
      toast.error('Failed to download save');
    }
  };

  const handleDeleteSave = async (save: CloudSave) => {
    const confirm = window.confirm(
      `Are you sure you want to delete this save?\n\nGame: ${
        save.romName || 'Unknown'
      }\nSlot: ${save.saveSlot}\n\nThis action cannot be undone.`
    );

    if (!confirm) return;

    try {
      await cloudSaveService.deleteSave(save.id);
      toast.success('Save deleted successfully');
      await loadSaves();
    } catch (error) {
      console.error('Failed to delete save:', error);
      toast.error('Failed to delete save');
    }
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (isLoading) {
    return (
      <div className="save-manager">
        <div className="loading">Loading save files...</div>
      </div>
    );
  }

  return (
    <div className="save-manager">
      <div className="section-header">
        <h2>Save Files</h2>
        <span className="storage-limit">{saves.length}/5 saves per ROM</span>
      </div>

      {saves.length === 0 ? (
        <div className="empty-state">
          <p>No save files found</p>
          <p className="empty-subtext">
            Save files will appear here after you play games
          </p>
        </div>
      ) : (
        <div className="saves-container">
          {Array.from(groupedSaves.entries()).map(([romHash, romSaves]) => {
            const romName = romSaves[0].romName || 'Unknown Game';
            return (
              <div key={romHash} className="rom-saves-group">
                <div className="rom-saves-header">
                  <h3>{romName}</h3>
                  <span className="save-count">
                    {romSaves.length}/5 saves
                  </span>
                </div>
                <div className="saves-grid">
                  {romSaves.map((save) => (
                    <div key={save.id} className="save-card">
                      <div className="save-info">
                        <div className="save-slot-badge">Slot {save.saveSlot}</div>
                        <p className="save-meta">
                          Size: {formatFileSize(save.fileSize)}
                        </p>
                        <p className="save-meta">
                          Last saved: {formatDate(save.updatedAt)}
                        </p>
                        <p className="save-meta">
                          Created: {formatDate(save.createdAt)}
                        </p>
                      </div>
                      <div className="save-actions">
                        <button
                          className="btn btn-primary btn-small"
                          onClick={() => handleDownloadSave(save)}
                          aria-label={`Download save slot ${save.saveSlot}`}
                        >
                          Download
                        </button>
                        <button
                          className="btn btn-danger btn-small"
                          onClick={() => handleDeleteSave(save)}
                          aria-label={`Delete save slot ${save.saveSlot}`}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {romSaves.length >= 5 && (
                  <p className="save-limit-warning">
                    Maximum save limit reached for this ROM. Delete a save to
                    create a new one.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
